import { useMutation, useQueryCache } from "react-query";
import { useToasts } from "react-toast-notifications";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import {
  DeleteNotifyContactSubscriptionPreferences,
  deleteNotifyContactSubscriptionPreferences,
} from "../../../queries/notify";
import { buildNotifyInfiniteSubscriberContactMethodsQueryKey } from "./useInfiniteNotifySubscriberContactMethods";
import { buildNotifySubscriberContactMethodsQueryKey } from "./useNotifySubscriberContactMethodsForLoggedInUser";
import { ToastStatuses } from "../../../components/global_components/Toast";

// removes a single notify contact from a single subscription group
export function useNotifyContactRemoveFromGroup({
  onSuccess = () => {},
  onError = () => {},
}: {
  onSuccess?: (subscriberContractMethod: DeleteNotifyContactSubscriptionPreferences) => void | Promise<void>;
  onError?: () => void;
} = {}) {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const { addToast } = useToasts();

  const [
    deleteNotifyContactSubscriptionPreferencesMutation,
    { isLoading: isDeleteNotifyContactSubscriptionPreferencesLoading },
  ] = useMutation(deleteNotifyContactSubscriptionPreferences(networkHeaders), {
    onSuccess: async (data) => {
      await queryCache.invalidateQueries(buildNotifyInfiniteSubscriberContactMethodsQueryKey());
      await queryCache.invalidateQueries(buildNotifySubscriberContactMethodsQueryKey());
      onSuccess(data);
    },
    onError: () => {
      addToast("Failed to remove user from group", { appearance: ToastStatuses.Error, autoDismiss: true });
      onError();
    },
  });

  const removeNotifyContactFromGroup = async (ids: number[], subscriptionGroupId: number) => {
    return await deleteNotifyContactSubscriptionPreferencesMutation({
      subscriptionGroupId,
      subscriberContactMethodIds: ids,
    });
  };

  return {
    removeNotifyContactFromGroup,
    isDeleteNotifyContactSubscriptionPreferencesLoading,
  };
}
